import PropTypes from "prop-types";
import * as React from "react";
import { DropdownMenu } from "react-ui";

import { HEADER_MENU_CLICK } from "../../../metricsAction";
import { MetricsActionPayload, trackAction } from "../../../Internals/Metrics/MetricsProvider";
import { SortEnum, SortType } from "../../MultiLine/MultiLineTypes";
import { TourTargetElement } from "../../../Internals/TourManagement/TourTargetElement";
import { AbsoluteModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { Icon } from "../../../Icons/Icon";

import { ChildItem, Head, IconWrapper, Root } from "./HeaderMenu.styles";

export interface HeaderMenuProps {
    type: string;
    children: React.ReactNode;
    filtered: boolean;
    sorted: SortType | undefined;
    path: AbsoluteModelPath;
    inputPlaceholder: string;
    header: string | JSX.Element;
    menuWidth?: number | string;
}

interface HeaderMenuState {
    opened: boolean;
}

type HeaderMenuIcon =
    | "ArrowCDownIcon"
    | "UiFilterFunnelIcon"
    | "UiFilterSortALowToHighIcon"
    | "UiFilterSortAHighToLowIcon";

function headerMenuSelector(props: HeaderMenuProps): MetricsActionPayload {
    if (typeof props.header === "string") {
        return { header: props.header };
    }

    return {};
}

export class HeaderMenu extends React.Component<HeaderMenuProps, HeaderMenuState> {
    public static childContextTypes = {
        menuWidth: PropTypes.number,
    };

    public state: HeaderMenuState = {
        opened: false,
    };

    public getChildContext = () => ({
        menuWidth: this.props.menuWidth,
    });

    public render(): JSX.Element {
        const caption = (
            <div>
                <Head opened={this.state.opened}>
                    {this.props.header}{" "}
                    <IconWrapper filtered={this.props.filtered}>
                        <Icon name={this.getIconName()} size={12} />
                    </IconWrapper>
                </Head>
            </div>
        );
        const children = React.Children.map(this.props.children ?? [], child => (
            <ChildItem>
                {!React.isValidElement<{ multilinePath: AbsoluteModelPath }>(child)
                    ? child
                    : React.cloneElement(child, { multilinePath: this.props.path })}
            </ChildItem>
        ));

        return (
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            <TourTargetElement controlId={`header_menu_${this.props.header}`}>
                <Root>
                    <DropdownMenu
                        menuMaxHeight={340}
                        menuWidth={this.props.menuWidth}
                        caption={caption}
                        onOpen={this.handleOnOpen}
                        onClose={this.handleOnClose}>
                        {children}
                    </DropdownMenu>
                </Root>
            </TourTargetElement>
        );
    }

    public shouldComponentUpdate(nextProps: HeaderMenuProps, nextState: HeaderMenuState): boolean {
        return (
            this.props.sorted !== nextProps.sorted ||
            this.props.filtered !== nextProps.filtered ||
            this.state.opened !== nextState.opened
        );
    }

    private getIconName(): HeaderMenuIcon {
        const { filtered, sorted } = this.props;

        if (filtered) {
            return "UiFilterFunnelIcon";
        }

        if (sorted) {
            return sorted === SortEnum.Asc ? "UiFilterSortALowToHighIcon" : "UiFilterSortAHighToLowIcon";
        }

        return "ArrowCDownIcon";
    }

    @trackAction(HEADER_MENU_CLICK, headerMenuSelector)
    private readonly handleOnOpen = (): void => {
        this.setState({ opened: true });
    };

    private readonly handleOnClose = () => {
        this.setState({ opened: false });
    };
}
